import { useMemo } from 'react';
import { EnvironmentData, ExternalLogger } from '@cld/unicorn-api';
import { createCloudinaryFetchClient } from '../../api/createCloudinaryFetchClient';
import { State } from '../state';
import { AbTestingClientState, ConsoleLogger, GetAuthenticationToken, LocationApi } from '../types';
import { createConsoleApiClient } from '../../api/createConsoleApiClient';
import { AbTestingClientFactory } from '../../api/createAbTestingClient';

export const useCreateDependencies = ({
  abTestingClientFactory,
  consoleLogger,
  environmentData,
  getAuthenticationToken,
  localStorageClient,
  locationApi,
  unicornExternalLogger,
  state,
}: {
  abTestingClientFactory: AbTestingClientFactory;
  consoleLogger: ConsoleLogger;
  environmentData: EnvironmentData;
  getAuthenticationToken: GetAuthenticationToken;
  localStorageClient: Storage;
  locationApi: LocationApi;
  unicornExternalLogger: ExternalLogger;
  state: State;
}) => {
  const productEnvironmentExternalId = state.applicationStatus === 'ready' ? state.productEnvironment.externalId : undefined;
  const cloudinaryFetchClient = useMemo(() => {
    if (productEnvironmentExternalId) {
      consoleLogger.debug('api', 'Product environment external id attached to fetch client', {
        productEnvironmentExternalId,
      });
    }

    return createCloudinaryFetchClient({
      // FIXME: inject fetch
      fetchFunction: window.fetch,
      pageOrigin: environmentData.origin,
      productEnvironmentExternalId,
      storage: localStorageClient,
      getAuthenticationToken,
      redirect: locationApi.redirect,
      consoleLogger,
    })();
  }, [productEnvironmentExternalId, getAuthenticationToken, localStorageClient, environmentData, consoleLogger, locationApi]);
  const unicornApiCreateCloudinaryFetchClient = useMemo(
    () =>
      createCloudinaryFetchClient({
        fetchFunction: window.fetch,
        pageOrigin: environmentData.origin,
        productEnvironmentExternalId,
        storage: localStorageClient,
        getAuthenticationToken,
        redirect: locationApi.redirect,
        consoleLogger,
      }),
    [productEnvironmentExternalId, getAuthenticationToken, localStorageClient, environmentData, consoleLogger, locationApi]
  );

  const consoleApiClient = useMemo(
    () =>
      createConsoleApiClient({
        backendUrl: environmentData.backendUrls.console,
        consoleLogger,
        unicornExternalLogger,
        cloudinaryFetchClient,
      }),
    [cloudinaryFetchClient, consoleLogger, unicornExternalLogger, environmentData]
  );

  const account = state.applicationStatus === 'ready' ? state.account : undefined;
  const productEnvironment = state.applicationStatus === 'ready' ? state.productEnvironment : undefined;
  const user = state.applicationStatus === 'ready' ? state.user : undefined;
  const abTestingClientState: AbTestingClientState = useMemo(() => {
    if (account && productEnvironment && user) {
      return {
        status: 'ready',
        client: abTestingClientFactory({
          account,
          productEnvironment,
          user,
        }),
      };
    }

    return { status: 'loading' };
  }, [account, productEnvironment, user, abTestingClientFactory]);

  return { consoleApiClient, abTestingClientState, unicornApiCreateCloudinaryFetchClient };
};
